<template>
  <div>
    <Navbar/>
      <div class="manufacturing-dashboard-wrapper">
      <div class="container custom-container">
        
        <div class="tabs-nav">
          <button 
            v-for="tab in tabsList" 
            :key="tab"
            class="tab-btn"
            :class="{ active: activeTab === tab }"
            @click="changeTab(tab)"
          >
            {{ tab }}
          </button>
        </div>

        <div class="hero-section" v-if="currentData">
          <div class="hero-image">
            <img :src="currentData.image" :alt="currentData.title" />
          </div>
          <div class="hero-content">
            <h2 class="hero-title">{{ currentData.title }}</h2>
            <p class="hero-desc">{{ currentData.desc }}</p>
          </div>
        </div>

        <div class="toggle-group" v-if="currentData">
          <button 
            v-for="toggle in currentData.toggles" 
            :key="toggle"
            class="toggle-btn"
            :class="{ active: activeToggle === toggle }"
            @click="activeToggle = toggle"
          >
            {{ toggle }}
          </button>
        </div>

        <div class="dynamic-content" v-if="currentData">
          
          <div class="materials-view" v-if="activeToggle === 'Materials'">
            <CncMaterials v-if="activeTab === 'CNC Machining'" />
            <PrintingMaterials v-if="activeTab === '3D Printing'" />
            <SheetMetalMaterials v-if="activeTab === 'Sheet Metal'" />
            <DieCastMaterials v-if="activeTab === 'Die Casting'" />
            
            <div v-if="activeTab === 'Compression Molding'">
              <CompressionMaterials />
              <p class="footer-note process-footer-note">
                *For materials not shown here, <span class="link-primary" @click="$router.push('/contact')">please contact us directly.</span>
              </p>
            </div>
          </div>

          <div class="finishes-view" v-if="activeToggle === 'Finishes' || activeToggle === 'Processes'">
            
            <CncFinishes v-if="activeTab === 'CNC Machining'" />
            
            <div class="force-3d-styles-wrapper" v-if="activeTab === '3D Printing'">
              <PrintingFinishes />
              <p class="footer-note process-footer-note">
                *For process not shown here, <span class="link-primary" @click="$router.push('/contact')">please contact us directly.</span>
              </p>
            </div>

            <SheetMetalFinishes v-if="activeTab === 'Sheet Metal'" />
            
            <DieCastFinishes v-if="activeTab === 'Die Casting' && activeToggle === 'Finishes'" />
            
            <div class="force-3d-styles-wrapper" v-if="activeTab === 'Die Casting' && activeToggle === 'Processes'">
              <DieCastProcess />
              <p class="footer-note process-footer-note">
                *For process not shown here, <span class="link-primary" @click="$router.push('/contact')">please contact us directly.</span>
              </p>
            </div>
            
            <div class="force-3d-styles-wrapper" v-if="activeTab === 'Compression Molding'">
              <CompressionFinishes />
              <p class="footer-note process-footer-note">
                *For Surface Finishes not listed here, <span class="link-primary" @click="$router.push('/contact')">please contact us directly.</span>
              </p>
            </div>

          </div>

        </div>

      </div>
    </div>
    <Footer />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';

import CncMaterials from '../front/CNC_Machining/components/Materials.vue'; 
import CncFinishes from '../front/CNC_Machining/components/FinishingOptionsPage.vue';   
import PrintingMaterials from '../front/3d-printing/components/printing_Materials.vue';
import PrintingFinishes from '../front/3d-printing/components/printing_Services.vue';
import SheetMetalMaterials from '../front/Sheet-metal/components/Sheet_Materials.vue';
import SheetMetalFinishes from '../front/Sheet-metal/components/Sheet_FinishingOptions.vue';
import DieCastMaterials from '../front/Die-Casting/components/Die_Materials.vue';
import DieCastFinishes from '../front/Die-Casting/components/Die_FinishingOptions.vue';
import DieCastProcess from '../front/Die-Casting/components/Die_Services.vue';
import CompressionMaterials from '../front/Compression_Molding/components/Compression_Materials.vue';
import CompressionFinishes from '../front/Compression_Molding/components/Compression_Services.vue';

import Footer from '../front/components/Footer.vue';
import Navbar from '../front/components/Nav.vue';

const tabsList = [
  'CNC Machining', '3D Printing', 
  'Sheet Metal', 'Die Casting', 'Compression Molding'
];

const activeTab = ref('CNC Machining');
const activeToggle = ref('Materials');

const database = {
  'CNC Machining': {
    title: 'Quality CNC Machining Services',
    desc: 'Accelerate prototyping, product development and production with a faster, easier way to get precision machined parts.',
    image: require('@/assets/images/home/1.jpg'), 
    toggles: ['Materials', 'Finishes']
  },
  '3D Printing': {
    title: 'Fast 3D Printing Services',
    desc: 'XCL makes it fast and easy to get high-quality 3D printed parts – no matter what stage your project is at.',
    image: require('@/assets/images/home/2.jpg'), 
    toggles: ['Materials', 'Processes']
  },
  'Sheet Metal': {
    title: 'Sheet Metal Services',
    desc: 'XCL’s sheet metal solutions deliver the consistency, reliability, durability, and quality you expect from XCL, including material and finishing options to support your applications.',
    image: require('@/assets/images/home/3.jpg'), 
    toggles: ['Materials', 'Finishes']
  },
  'Die Casting': {
    title: 'Die Casting Services',
    desc: 'Create high quality custom mechanicals with precision and accuracy.',
    image: require('@/assets/images/home/5.jpg'), 
    toggles: ['Processes', 'Materials', 'Finishes']
  },
  'Compression Molding': {
    title: 'Compression Molding Services',
    desc: 'Experience lower tooling costs than injection molding with high-quality, durable parts.',
    image: require('@/assets/images/home/4.jpg'), 
    toggles: ['Materials', 'Finishes']
  }
};

const currentData = computed(() => database[activeTab.value] || null);

const changeTab = (tab) => {
  activeTab.value = tab;
};

watch(activeTab, (newTab) => {
  const data = database[newTab];
  if (data) {
    activeToggle.value = data.toggles[0];
  }
}, { immediate: true });
</script>

<style scoped>
/* ========================================================
   全局外层框架 (电脑端原样保留)
======================================================== */
.manufacturing-dashboard-wrapper {
  display: block !important;
  width: 100% !important;
  background-color: #ffffff;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  padding: 20px 20px 100px 20px;
  color: #333;
  box-sizing: border-box !important;
}

.custom-container {
  display: block !important;
  max-width: 1100px !important;
  margin: 0 auto !important;
  width: 100% !important;
}

/* --- 全局公用链接颜色 --- */
.link-primary { color: #16bc9c; text-decoration: none; font-weight: 500; }
.link-primary:hover { text-decoration: underline; }

/* --- 1. 顶部 Tabs --- */
.tabs-nav {
  display: flex !important; flex-direction: row !important; justify-content: space-between !important;
  border-bottom: 1px solid #eaeaea; margin-bottom: 40px; width: 100% !important;
  overflow-x: auto; overflow-y: hidden; scrollbar-width: none; -ms-overflow-style: none; 
}
.tabs-nav::-webkit-scrollbar { display: none; }

.tab-btn {
  background: none; border: none; padding: 16px 8px; font-size: 15px; color: #555;
  cursor: pointer; position: relative; white-space: nowrap; transition: all 0.2s ease;
}
.tab-btn:hover { color: #111; }
.tab-btn.active { color: #111; font-weight: 600; }
.tab-btn.active::after {
  content: ''; position: absolute; bottom: -1px; left: 0; width: 100%; height: 2px; background-color: #16bc9c;
}

/* --- 2. 主视觉区 Hero Section --- */
.hero-section {
  display: flex !important; flex-direction: row !important; gap: 40px !important;
  align-items: flex-start !important;
  margin-bottom: 40px !important; width: 100% !important;
}
.hero-image { 
  flex: 0 0 40%; 
  max-width: 40%; 
}
.hero-image img { 
  width: 100%; aspect-ratio: 16 / 9; 
  object-fit: contain; 
  max-height: 240px; 
  background-color: #ffffff; 
  border-radius: 8px; border: 1px solid #eaeaea; display: block; 
}
.hero-content { 
  flex: 1; text-align: left; 
  padding-top: 16px; 
}
.hero-title { font-size: 32px; font-weight: 700; color: #111; margin-bottom: 16px; line-height: 1.3; }
.hero-desc { font-size: 16px; color: #666; line-height: 1.6; }

/* --- 3. 二级切换按钮 Toggle Group --- */
.toggle-group {
  display: flex !important; justify-content: center !important; gap: 16px !important;
  margin-bottom: 40px !important; width: 100% !important;
}
.toggle-btn {
  background-color: #fff; border: 1px solid #dcdcdc; border-radius: 4px; padding: 12px 40px;
  font-size: 16px; font-weight: 500; color: #333; cursor: pointer; transition: all 0.2s ease;
}
.toggle-btn:hover { border-color: #999; }
.toggle-btn.active { border-color: #16bc9c; color: #111; }

.dynamic-content { width: 100% !important; display: block !important; }

/* ========================================================
   正常表格组件样式抹平 (防止多容器嵌套导致内边距过大)
======================================================== */
:deep(.finishing-options-page),
:deep(.materials-page),
:deep(.finishing-options-page .container) {
  padding: 0 !important; margin: 0 !important; min-width: 100% !important;
  max-width: 100% !important; width: 100% !important; box-sizing: border-box !important;
}

:deep(.finishing-options-page .page-title),
:deep(.materials-page .section-title) {
  display: block !important; margin-top: 0px !important; margin-bottom: 30px !important;
  text-align: center !important; font-size: 26px !important; font-weight: 700 !important; color: #111 !important;        
}

/* --- 3D打印/工艺底部提示的样式 (电脑端原有样式) --- */
.process-footer-note {
  text-align: left;
  color: #666;
  font-size: 15px;
  margin-top: 15px;
  padding-bottom: 20px;
  position: relative;
  left: 19px;
}

/* ========================================================
   移动端/平板端专属适配 (严格被隔离在此媒体查询内，绝不影响电脑端)
======================================================== */
@media (max-width: 992px) {
  /* 平板端将卡片强制降级为2列 */
  .force-3d-styles-wrapper :deep(.services-grid) {
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 20px !important;
  }
}

@media (max-width: 768px) {
  /* 外部边距收紧 */
  .manufacturing-dashboard-wrapper { padding: 15px 15px 60px 15px !important; }

  /* Hero 区域：改为垂直排列、文字居中 */
  .hero-section { flex-direction: column !important; min-height: auto; align-items: center !important; gap: 20px !important; margin-bottom: 30px !important; }
  .hero-image { max-width: 100%; flex: 1 1 100%; width: 100%; }
  .hero-image img { max-height: 200px !important; }
  .hero-content { padding-top: 0; text-align: center; }
  .hero-title { font-size: 24px !important; margin-bottom: 12px !important; }
  .hero-desc { font-size: 14px !important; }

  /* 顶部 Tabs 优化：间距调整让滑动更顺畅 */
  .tabs-nav { justify-content: flex-start !important; margin-bottom: 25px !important; }
  .tab-btn { font-size: 14px !important; padding: 12px 10px !important; }

  /* 二级按钮 Toggle 优化：允许换行、减小内边距防止超框 */
  .toggle-group { flex-wrap: wrap !important; gap: 10px !important; margin-bottom: 30px !important; }
  .toggle-btn { padding: 10px 20px !important; font-size: 14px !important; flex: 1; min-width: 110px; white-space: nowrap; text-align: center; }

  /* 底部注释文字优化：取消强制左偏移，居中对齐 */
  .process-footer-note { left: 0 !important; text-align: center !important; font-size: 13px !important; }
  
  /* 深层穿透修改引用的子组件标题大小 */
  :deep(.finishing-options-page .page-title),
  :deep(.materials-page .section-title) { font-size: 22px !important; margin-bottom: 20px !important; }

  /* 子组件手机端卡片强制1列，防止图片挤压 */
  .force-3d-styles-wrapper :deep(.services-grid) {
    grid-template-columns: 1fr !important;
    gap: 15px !important;
  }
  .force-3d-styles-wrapper :deep(.card-image img) {
    width: 100% !important;
    height: auto !important;
    object-fit: cover !important;
  }
}

/* ========================================================
   通用卡片整容魔法 (适用于 3D, Die Casting, Compression)
======================================================== */
/* 🌟 新增：强力清除所有子组件根节点、section、container 自带的灰色背景 */
.force-3d-styles-wrapper :deep(> div),
.force-3d-styles-wrapper :deep(section),
.force-3d-styles-wrapper :deep(.container),
.force-3d-styles-wrapper :deep(.cnc-services-section),
.force-3d-styles-wrapper :deep([class*="bg"]) {
    background-color: transparent !important;
    background: none !important;
    padding: 0 !important;
}

/* 通用网格设置：间距固定 */
.force-3d-styles-wrapper :deep(.services-grid) {
    gap: 20px !important;
}

/* 卡片通用变身：加上 8px 圆角、柔和阴影，去掉直角 */
.force-3d-styles-wrapper :deep(.service-card) {
    border-radius: 8px !important;
    box-shadow: 0 4px 12px rgba(0,0,0,0.05) !important;
    border: 1px solid #eaeaea !important;
    background-color: #ffffff !important; /* 确保卡片本身是白色的 */
}
.force-3d-styles-wrapper :deep(.service-card:hover) {
    box-shadow: 0 10px 25px rgba(0,0,0,0.1) !important;
}

/* 图片部分加上顶部圆角，贴合卡片边缘 */
.force-3d-styles-wrapper :deep(.card-image),
.force-3d-styles-wrapper :deep(.card-image img) {
    border-radius: 8px 8px 0 0 !important;
}

/* 修改文字样式：破解子组件原有的高度限制 */
.force-3d-styles-wrapper :deep(.card-content) {
    padding: 20px !important;
}

.force-3d-styles-wrapper :deep(.card-title) {
    font-size: 16px !important;
    height: auto !important; 
    margin-bottom: 12px !important;
    white-space: normal !important;
}

.force-3d-styles-wrapper :deep(.card-desc) {
    font-size: 14px !important;
    font-weight: 400 !important;
    color: #555 !important;
}

/* Compression Molding 里可能自带的展开按钮样式保留美化 */
.force-3d-styles-wrapper :deep(.toggle-btn) {
    margin-top: auto !important; 
}

/* 强行隐藏所有底部的 Get Instant Quote 绿底大按钮 */
.force-3d-styles-wrapper :deep(.action-buttons) {
    display: none !important;
}
</style>